import React, { useState } from 'react';
import { Modal, Form, Button, Alert } from 'react-bootstrap';
import { writeContract, waitForTransactionReceipt } from '@wagmi/core';
import * as config from '../../config.tsx'; // Import your config

interface ProposeParamGroupModalProps {
  show: boolean;
  onHide: () => void;
  abi: any[]; // ABI for encoding
  contractAddress: `0x${string}`;
  groupName: string;
  communityName: string; // Passed community name
}

const ProposeParamGroupModal: React.FC<ProposeParamGroupModalProps> = ({
  show,
  onHide,
  abi,
  contractAddress,
  groupName,
  communityName,
}) => {
  const [formValues, setFormValues] = useState<Record<string, any>>({});
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  // Get the "createProposal" method from the ABI
  const createProposalAbi = abi.find((method) => method.name === 'createProposal');

  if (!createProposalAbi) {
    return <Alert variant="danger">ABI does not contain the createProposal function.</Alert>;
  }

  const inputs = createProposalAbi.inputs; // Get inputs for the createProposal method

  // Handle form input changes
  const handleFormChange = (key: string, value: string) => {
    setFormValues((prevValues) => ({ ...prevValues, [key]: value }));
  };

  // Cast input values based on the type defined in the ABI
  const castValueByType = (value: string, type: string): any => {
    switch (type) {
      case 'uint256':
        return BigInt(value); // Cast to BigInt for uint256
      case 'uint8':
        return Number(value); // Cast to Number for uint8
      case 'address':
        return value; // Keep as string for addresses
      case 'bool':
        return value === 'true'; // Convert string to boolean
      default:
        return value; // Keep as string for everything else
    }
  };

  // Handle the form submission
  const handleSubmit = async () => {
    try {
      setLoading(true);

      // Create an array of values casted to their respective types
      const values = inputs.map((input) => {
        const value = formValues[input.name] || '';
        return castValueByType(value, input.type);
      });

      console.log('Submitting values:', values);

      // Execute the write contract operation using createProposal
      const tx = await writeContract(config.config, {
        address: contractAddress!,
        abi,
        functionName: 'createProposal',
        args: [ ...values], // First argument is the community name
      });

      // Wait for transaction receipt
      await waitForTransactionReceipt(config.config, {
        hash: tx,
      });

      alert('Parameters updated successfully!');
      onHide();
    } catch (err) {
      console.error('Error setting parameters:', err);
      setError('Error setting parameters. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>Propose Parameters for {groupName}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        <Form>
          {inputs.map((input, index) => (
            <Form.Group key={index}>
              <Form.Label>{input.name} ({input.type})</Form.Label>
              <Form.Control
                type="text"
                value={formValues[input.name] || ''}
                onChange={(e) => handleFormChange(input.name, e.target.value)}
                placeholder={`Enter value for ${input.name}`}
              />
            </Form.Group>
          ))}
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={loading}
            className="mt-3"
          >
            {loading ? 'Setting...' : 'Set Parameters'}
          </Button>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default ProposeParamGroupModal;
